// frontend/src/components/upload/ProgressIndicator.jsx
import React from 'react';
import { Upload, Database, CheckCircle } from 'lucide-react';

const ProgressIndicator = ({ progress, status, fileName }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'upload':
        return {
          icon: Upload,
          title: 'Téléchargement en cours...',
          description: `Upload de ${fileName}`,
          color: 'blue'
        };
      case 'processing':
        return {
          icon: Database,
          title: 'Analyse des données...',
          description: 'Validation et détection des anomalies',
          color: 'purple'
        };
      case 'complete':
        return {
          icon: CheckCircle,
          title: 'Traitement terminé',
          description: 'Données prêtes pour l\'analyse TURPE',
          color: 'green'
        };
      default:
        return {
          icon: Upload,
          title: 'Traitement...',
          description: '',
          color: 'blue'
        };
    }
  };

  const { icon: Icon, title, description, color } = getStatusConfig();

  const getColorClasses = (baseColor) => {
    const colors = {
      blue: {
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        progress: 'bg-blue-600'
      },
      purple: {
        bg: 'bg-purple-100',
        text: 'text-purple-600',
        progress: 'bg-purple-600'
      },
      green: {
        bg: 'bg-green-100',
        text: 'text-green-600',
        progress: 'bg-green-600'
      }
    };
    return colors[baseColor] || colors.blue;
  };

  const colorClasses = getColorClasses(color);

  return (
    <div className="space-y-6">
      
      {/* Icône et statut */}
      <div className="text-center">
        <div className={`w-20 h-20 rounded-full ${colorClasses.bg} flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-10 h-10 ${colorClasses.text}`} />
        </div>
        
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {title}
        </h3>
        
        {description && (
          <p className="text-gray-600">
            {description}
          </p>
        )}
      </div>

      {/* Barre de progression */}
      <div className="w-full max-w-md mx-auto space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Progression</span>
          <span className={`font-medium ${colorClasses.text}`}>
            {Math.round(progress)}%
          </span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className={`h-full ${colorClasses.progress} transition-all duration-300 ease-out`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Étapes du processus */}
      <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
        {[
          { step: 'Upload', threshold: 0 },
          { step: 'Validation', threshold: 70 },
          { step: 'Analyse', threshold: 90 }
        ].map(({ step, threshold }, index) => (
          <div
            key={step}
            className={`text-center p-3 rounded-lg border-2 transition-all ${
              progress > threshold
                ? `border-${color}-200 bg-${color}-50`
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className={`w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center text-sm font-bold ${
              progress > threshold
                ? `bg-${color}-600 text-white`
                : 'bg-gray-300 text-gray-600'
            }`}>
              {progress > threshold ? '✓' : index + 1}
            </div>
            <div className={`text-sm font-medium ${
              progress > threshold ? colorClasses.text : 'text-gray-600'
            }`}>
              {step}
            </div>
          </div>
        ))}
      </div>

      {/* Conseils pendant l'attente */}
      <div className="bg-gray-50 rounded-xl p-6 max-w-lg mx-auto">
        <h4 className="font-medium text-gray-900 mb-3">
          💡 Pendant que nous analysons vos données...
        </h4>
        <ul className="space-y-2 text-sm text-gray-600">
          <li>• Nous vérifions la qualité et cohérence des données</li>
          <li>• Détection automatique des anomalies et gaps</li>
          <li>• Classification des périodes temporelles TURPE</li>
          <li>• Calcul des statistiques de consommation</li>
        </ul>
      </div>
    </div>
  );
};

export default ProgressIndicator;
